import { useMemo } from 'react';
import { useQueryState, useMutation, toast } from '@ywwwtseng/react-kit';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { useTonWalletKit } from '@ywwwtseng/ton-wallet-kit';
import * as schema from '@/db/schema';
import { WALLET_ID, NETWORKS } from '@/constants';

export function useWallets() {
  const wallets =
    useQueryState<(typeof schema.wallets.$inferSelect)[]>('wallets');
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const { mutate: updateMe } = useMutation('me:update');
  const walletKitConnect = useWalletKitConnect();
  const tonWalletKit = useTonWalletKit();

  return useMemo(() => {
    return {
      connected: {
        [NETWORKS.SOLANA]: walletKitConnect.accounts.solana.address,
        [NETWORKS.BSC]: walletKitConnect.accounts.bsc.address,
        [NETWORKS.TON]: tonWalletKit.address,
        [NETWORKS.ETHEREUM]: walletKitConnect.accounts.ethereum.address,
      },
      me: {
        [NETWORKS.SOLANA]: me?.sol_address,
        [NETWORKS.BSC]: me?.bsc_address,
        [NETWORKS.TON]: me?.ton_address,
        [NETWORKS.ETHEREUM]: me?.eth_address,
      },
      offical: {
        [NETWORKS.SOLANA]: wallets?.find(
          (wallet) => wallet.id === WALLET_ID.OFFICIAL_SOLANA_WALLET
        ),
        [NETWORKS.BSC]: wallets?.find(
          (wallet) => wallet.id === WALLET_ID.OFFICIAL_BSC_WALLET
        ),
        [NETWORKS.TON]: wallets?.find(
          (wallet) => wallet.id === WALLET_ID.OFFICIAL_TON_WALLET
        ),
        [NETWORKS.ETHEREUM]: wallets?.find(
          (wallet) => wallet.id === WALLET_ID.OFFICIAL_ETHEREUM_WALLET
        ),
      },
      sync: async (network: string) => {
        if (network === NETWORKS.SOLANA) {
          if (!walletKitConnect.accounts.solana) {
            toast.error('Solana address not set');
            throw new Error('Solana address not set');
          }
          await updateMe({ sol_address: walletKitConnect.accounts.solana.address });
          return walletKitConnect.accounts.solana;
        } else if (network === NETWORKS.BSC) {
          if (!walletKitConnect.accounts.bsc) {
            toast.error('BSC address not set');
            throw new Error('BSC address not set');
          }
          await updateMe({ bsc_address: walletKitConnect.accounts.bsc.address });
          return walletKitConnect.accounts.bsc;
        } else if (network === NETWORKS.ETHEREUM) {
          if (!walletKitConnect.accounts.ethereum) {
            toast.error('Ethereum address not set');
            throw new Error('Ethereum address not set');
          }
          await updateMe({ eth_address: walletKitConnect.accounts.ethereum.address });
          return walletKitConnect.accounts.ethereum;
        } else if (network === NETWORKS.TON) {
          if (!tonWalletKit.address) {
            toast.error('TON address not set');
            throw new Error('TON address not set');
          }
          await updateMe({ ton_address: tonWalletKit.address });
          return tonWalletKit.address;
        } else {
          throw new Error('Invalid network');
        }
      },
    };
  }, [me, wallets, walletKitConnect.accounts, tonWalletKit.address]);
}
